/**
 * Event Dispatcher module is used for registering listeners and dispatching
 * events across amCharts system.
 */

/**
 * ============================================================================
 * IMPORTS
 * ============================================================================
 * @hidden
 */
import { Disposer, IDisposer, MultiDisposer } from "./Disposer";
import * as $array from "./Array";
import * as $type from "./Type";

/**
 * @ignore
 */
export type Events<Target, T> = {
	[K in keyof T]: T[K] & { type: K, target: Target }
};

/**
 * A universal interface for event listeners.
 *
 * @ignore
 */
export interface EventListener {
	killed: boolean;
	once: boolean;
	type: any | null;
	callback: (event: any) => void;
	context: unknown;
	shouldClone: boolean;
	dispatch: (type: any, event: any) => void;
	disposer: IDisposer;

	_debounceDelay?: number
	_debounceTimeout?: number;
}

/**
 * Universal Event Dispatcher.
 *
 * @see {@link https://www.amcharts.com/docs/v5/concepts/events/} for more info
 */
export class EventDispatcher<T> implements IDisposer {
	protected _listeners: Array<EventListener>;
	protected _killed: Array<EventListener>;
	protected _disabled: { [key in keyof T]?: number };
	protected _iterating: number;
	protected _enabled: boolean;
	protected _disposed: boolean;


	/**
	 * Constructor
	 */
	constructor() {
		this._listeners = [];
		this._killed = [];
		this._disabled = {};
		this._iterating = 0;
		this._enabled = true;
		this._disposed = false;
	}

	/**
	 * Returns `true` if the dispatcher has been disposed.
	 *
	 * @return Disposed?
	 */
	public isDisposed(): boolean {
		return this._disposed;
	}

	/**
	 * Disposes the dispatcher and all its listeners.
	 */
	public dispose(): void {
		if (!this._disposed) {
			this._disposed = true;

			const a = this._listeners;

			this._iterating = 1;
			this._listeners = <any>null;
			this._disabled = <any>null;

			try {
				$array.each(a, (x) => {
					x.disposer.dispose();
				});

			} finally {
				this._killed = <any>null;
				this._iterating = <any>null;
			}
		}
	}

	/**
	 * Checks if the dispatcher has any listeners.
	 *
	 * @return Has listeners?
	 */
	public hasListeners(): boolean {
		return this._listeners.length !== 0;
	}

	/**
	 * Checks if the dispatcher has listeners for `type`, counting the ones added
	 * with `onAll()`.
	 *
	 * @param type  Event type
	 * @return Has particular event listeners?
	 */
	public hasListenersByType<Key extends keyof T>(type: Key): boolean {
		return $array.any(this._listeners, (x) => (x.type === null || x.type === type) && !x.killed);
	}

	/**
	 * Enable dispatching of events if they were previously disabled by
	 * `disable()`.
	 */
	public enable(): void {
		this._enabled = true;
	}

	/**
	 * Disable dispatching of events until re-enabled by `enable()`.
	 */
	public disable(): void {
		this._enabled = false;
	}

	/**
	 * Enable dispatching particular event, if it was disabled before by
	 * `disableType()`.
	 *
	 * @param type Event type
	 */
	public enableType<Key extends keyof T>(type: Key): void {
		delete this._disabled[type];
	}

	/**
	 * Stops dispatching events of `type`, until `enableType()` is called or, if
	 * `amount` is set, for that many dispatches.
	 *
	 * @param type    Event type
	 * @param amount  Number of event dispatches to skip
	 */
	public disableType<Key extends keyof T>(type: Key, amount: number = Infinity): void {
		this._disabled[type] = amount;
	}

	/**
	 * Removes listener from dispatcher.
	 *
	 * Will throw an exception if such listener does not exist.
	 *
	 * @param listener Listener to remove
	 */
	protected _removeListener(listener: EventListener): void {
		if (this._iterating === 0) {
			const index = this._listeners.indexOf(listener);

			if (index === -1) {
				throw new Error("Invalid state: could not remove listener");
			}

			this._listeners.splice(index, 1);

		} else {
			this._killed.push(listener);
		}
	}

	/**
	 * Removes existing listener by certain parameters.
	 *
	 * @param once         Listener's once setting
	 * @param type         Listener's type
	 * @param callback     Callback function
	 * @param context      Callback context
	 */
	protected _removeExistingListener<C, Key extends keyof T>(once: boolean, type: Key | null, callback?: (this: C, event: T[Key]) => void, context?: C): void {
		if (this._disposed) {
			throw new Error("EventDispatcher is disposed");
		}

		this._eachListener((info) => {
			if (info.once === once && // TODO is this correct ?
				info.type === type &&
				(callback === undefined || info.callback === callback) &&
				info.context === context) {
				info.disposer.dispose();
			}
		});
	}

	/**
	 * Checks if an event of `type` would reach a listener: events are enabled,
	 * the type is not disabled, and it has listeners.
	 *
	 * @param type  Event type
	 * @return Enabled?
	 */
	public isEnabled<Key extends keyof T>(type: Key): boolean {
		if (this._disposed) {
			throw new Error("EventDispatcher is disposed");
		}

		// TODO is this check correct ?
		return this._enabled && this._listeners.length > 0 && this.hasListenersByType(type) && this._disabled[type] === undefined;
	}

	/**
	 * Removes all listeners of a particular event type.
	 *
	 * @param type  Listener's type
	 */
	public removeType<Key extends keyof T>(type: Key): void {
		if (this._disposed) {
			throw new Error("EventDispatcher is disposed");
		}

		this._eachListener((info) => {
			if (info.type === type) {
				info.disposer.dispose();
			}
		});
	}

	/**
	 * Checks if there's already a listener with specific parameters. Listeners
	 * added with `once()` are not counted.
	 *
	 * @param type      Listener's type
	 * @param callback  Callback function
	 * @param context   Callback context
	 * @return Has listener?
	 */
	public has<C, Key extends keyof T>(type: Key, callback?: (this: C, event: T[Key]) => void, context?: C): boolean {
		const index = $array.findIndex(this._listeners, (info) => {
			return info.once !== true && // Ignoring "once" listeners
				info.type === type &&
				(callback === undefined || info.callback === callback) &&
				info.context === context;
		});

		return index !== -1;
	}

	/**
	 * Checks whether event of the particular type should be dispatched.
	 *
	 * @param type  Event type
	 * @return Dispatch?
	 */
	protected _shouldDispatch<Key extends keyof T>(type: Key): boolean {
		if (this._disposed) {
			throw new Error("EventDispatcher is disposed");
		}

		const count = this._disabled[type];

		if (!$type.isNumber(count)) {
			return this._enabled;

		} else {
			if (count <= 1) {
				delete this._disabled[type];

			} else {
				--this._disabled[type]!;
			}

			return false;
		}
	}

	/**
	 * Calls `fn` for each listener. Listeners removed meanwhile are taken out
	 * of the list after the loop.
	 *
	 * @param fn  Function to call
	 */
	protected _eachListener(fn: (listener: EventListener) => void): void {
		++this._iterating;

		try {
			$array.each(this._listeners, fn);

		} finally {
			--this._iterating;

			// TODO should this be inside or outside the finally ?
			if (this._iterating === 0 && this._killed.length !== 0) {
				// Remove killed listeners
				$array.each(this._killed, (killed) => {
					this._removeListener(killed);
				});

				this._killed.length = 0;
			}
		}
	}

	/**
	 * Calls the listeners of `type` right away; debounced listeners are called
	 * after their delay. `event` must include `type` and `target`.
	 *
	 * @param type   Event type
	 * @param event  Event object
	 */
	public dispatch<Key extends keyof T>(type: Key, event: T[Key]): void {
		if (this._shouldDispatch(type)) {
			// TODO check if it's faster to use an object of listeners rather than a single big array
			// TODO if the function throws, maybe it should keep going ?
			this._eachListener((listener) => {
				if (!listener.killed && (listener.type === null || listener.type === type)) {
					if (listener._debounceDelay) {
						if (listener._debounceTimeout) {
							window.clearTimeout(listener._debounceTimeout);
						}
						listener._debounceTimeout = window.setTimeout(() => {
							listener._debounceTimeout = undefined;
							if (!listener.killed) {
								listener.dispatch(type, event);
							}
						}, listener._debounceDelay);
					}
					else {
						listener.dispatch(type, event);
					}
				}
			});
		}
	}

	/**
	 * Shelves the event to be dispatched within next update cycle.
	 *
	 * @param type   Event type
	 * @param event  Event object
	 * @todo automatically add in type and target properties if they are missing
	 */
	/*public dispatchLater<Key extends keyof T>(type: Key, event: T[Key]): void {
		if (this._shouldDispatch(type)) {
			this._eachListener((listener) => {
				// TODO check if it's faster to use an object of listeners rather than a single big array
				if (!listener.killed && (listener.type === null || listener.type === type)) {
					// TODO if the function throws, maybe it should keep going ?
					// TODO dispatch during the update cycle, rather than using whenIdle
					$async.whenIdle(() => {
						if (!listener.killed) {
							listener.dispatch(type, event);
						}
					});
				}
			});
		}
	}*/

	/**
	 * Creates, catalogs and returns an [[EventListener]], replacing an existing
	 * one with the same parameters.
	 *
	 * @param once           Listener's once setting
	 * @param type           Listener's type
	 * @param callback       Callback function
	 * @param context        Callback context
	 * @param shouldClone    Whether the listener should be copied when the EventDispatcher is copied
	 * @param dispatch       Function that calls `callback` for an event
	 * @param debounceDelay  Debounce delay in milliseconds
	 * @returns An event listener
	 */
	protected _on<C, Key extends keyof T>(once: boolean, type: Key | null, callback: any, context: C, shouldClone: boolean, dispatch: (type: Key, event: T[Key]) => void, debounceDelay?: number): EventListener {
		if (this._disposed) {
			throw new Error("EventDispatcher is disposed");
		}

		this._removeExistingListener(once, type, callback, context);

		const info: EventListener = {
			type: type,
			callback: callback,
			context: context,
			shouldClone: shouldClone,
			dispatch: <any>dispatch,
			killed: false,
			once: once,
			disposer: new Disposer(() => {
				info.killed = true;
				if (info._debounceTimeout) {
					window.clearTimeout(info._debounceTimeout);
				}
				this._removeListener(info);
			}),
			_debounceDelay: debounceDelay
		};

		this._listeners.push(info);

		return info;
	}

	/**
	 * Creates an event listener to be invoked on **any** event.
	 *
	 * @param callback     Callback function
	 * @param context      Value of `this` inside `callback`
	 * @param shouldClone  Whether the listener should be copied when the EventDispatcher is copied
	 * @returns A disposable event listener
	 */
	public onAll<C, K extends keyof T>(callback: (this: C, event: T[K]) => void, context?: C, shouldClone: boolean = true): IDisposer {
		return this._on(false, null, callback, context, shouldClone, (_type, event) => (<any>callback).call(context, event as any)).disposer;
	}

	/**
	 * Creates an event listener to be invoked on a specific event type.
	 *
	 * ```TypeScript
	 * button.events.on("click", (ev) => {
	 *   console.log("Button clicked");
	 * }, this);
	 * ```
	 * ```JavaScript
	 * button.events.on("click", function(ev) {
	 *   console.log("Button clicked");
	 * }, this);
	 * ```
	 *
	 * The above logs every click on the button. To remove the listener, call
	 * `dispose()` on the returned object, or use `off()`.
	 *
	 * @param type         Listener's type
	 * @param callback     Callback function
	 * @param context      Value of `this` inside `callback`
	 * @param shouldClone  Whether the listener should be copied when the EventDispatcher is copied
	 * @returns A disposable event listener
	 */
	public on<C, Key extends keyof T>(type: Key, callback: (this: C | undefined, event: T[Key]) => void, context?: C, shouldClone: boolean = true): IDisposer {
		return this._on(false, type, callback, context, shouldClone, (_type, event) => callback.call(context, event)).disposer;
	}

	/**
	 * Creates a debounced event listener to be invoked on a specific event type.
	 *
	 * ```TypeScript
	 * button.events.onDebounced("click", (ev) => {
	 *   console.log("Button clicked");
	 * }, 500, this);
	 * ```
	 * ```JavaScript
	 * button.events.onDebounced("click", function(ev) {
	 *   console.log("Button clicked");
	 * }, 500, this);
	 * ```
	 *
	 * The above logs once, 500 ms after the last of a series of clicks. Each new
	 * event restarts the delay, and `callback` gets the last one.
	 *
	 * @param type           Listener's type
	 * @param callback       Callback function
	 * @param debounceDelay  Debounce delay in milliseconds
	 * @param context        Value of `this` inside `callback`
	 * @param shouldClone    Whether the listener should be copied when the EventDispatcher is copied
	 * @returns A disposable event listener
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/events/#Debounced_events} for more info
	 * @since 5.14.0
	 */
	public onDebounced<C, Key extends keyof T>(type: Key, callback: (this: C | undefined, event: T[Key]) => void, debounceDelay: number, context?: C, shouldClone: boolean = true): IDisposer {
		return this._on(false, type, callback, context, shouldClone, (_type, event) => callback.call(context, event), debounceDelay).disposer;
	}

	/**
	 * Creates an event listener that is invoked only the first time the event
	 * happens, and is then disposed.
	 *
	 * ```TypeScript
	 * button.events.once("click", (ev) => {
	 *   console.log("Button clicked");
	 * }, this);
	 * ```
	 * ```JavaScript
	 * button.events.once("click", function(ev) {
	 *   console.log("Button clicked");
	 * }, this);
	 * ```
	 *
	 * The above logs the first click on the button only. `off()` does not
	 * remove such a listener: call `dispose()` on the returned object instead.
	 *
	 * @param type         Listener's type
	 * @param callback     Callback function
	 * @param context      Value of `this` inside `callback`
	 * @param shouldClone  Whether the listener should be copied when the EventDispatcher is copied
	 * @returns A disposable event listener
	 */
	public once<C, Key extends keyof T>(type: Key, callback: (this: C | undefined, event: T[Key]) => void, context?: C, shouldClone: boolean = true): IDisposer {
		const x = this._on(true, type, callback, context, shouldClone, (_type, event) => {
			x.disposer.dispose();
			callback.call(context, event)
		});

		// TODO maybe this should return a different Disposer ?
		return x.disposer;
	}

	/**
	 * Removes listeners added with `on()` or `onDebounced()` that match `type`,
	 * `callback` and `context`. Without `callback`, removes all of them for
	 * `type` and `context`. Listeners added with `once()` are not removed.
	 *
	 * @param type         Listener's type
	 * @param callback     Callback function
	 * @param context      Callback context, the same as passed to `on()`
	 */
	public off<C, Key extends keyof T>(type: Key, callback?: (this: C, event: T[Key]) => void, context?: C): void {
		this._removeExistingListener(false, type, callback, context);
	}


	/**
	 * Copies all dispatcher parameters, including listeners, from another event
	 * dispatcher.
	 *
	 * @param source Source event dispatcher
	 * @ignore
	 */
	public copyFrom(source: this): IDisposer {
		if (this._disposed) {
			throw new Error("EventDispatcher is disposed");
		}

		if (source === this) {
			throw new Error("Cannot copyFrom the same TargetedEventDispatcher");
		}

		const disposers: Array<IDisposer> = [];

		$array.each(source._listeners, (x) => {
			// TODO is this correct ?
			if (!x.killed && x.shouldClone) {
				if (x.type === null) {
					disposers.push(this.onAll(x.callback as any, x.context));

				} else if (x.once) {
					disposers.push(this.once(x.type, x.callback, x.context));

				} else {
					disposers.push(this.on(x.type, x.callback, x.context));
				}
			}
		});

		return new MultiDisposer(disposers);
	}

}

/**
 * A version of the [[EventDispatcher]] that dispatches events for a specific
 * target object.
 *
 * @ignore
 */
export class TargetedEventDispatcher<Target, T> extends EventDispatcher<T> {

	/**
	 * A target object which is originating events using this dispatcher.
	 */
	public target: Target;

	/**
	 * Constructor
	 *
	 * @param target Event dispatcher target
	 */
	constructor(target: Target) {
		super();
		this.target = target;
	}

	/**
	 * Copies all dispatcher parameters, including listeners, from another event
	 * dispatcher.
	 *
	 * @param source Source event dispatcher
	 * @ignore
	 */
	public copyFrom(source: this): IDisposer {
		if (this._disposed) {
			throw new Error("EventDispatcher is disposed");
		}

		if (source === this) {
			throw new Error("Cannot copyFrom the same TargetedEventDispatcher");
		}

		const disposers: Array<IDisposer> = [];

		$array.each(source._listeners, (x) => {
			// TODO very hacky
			if (x.context === source.target) {
				return;
			}
			// TODO is this correct ?
			if (!x.killed && x.shouldClone) {
				if (x.type === null) {
					disposers.push(this.onAll(x.callback as any, x.context));

				} else if (x.once) {
					disposers.push(this.once(x.type, x.callback, x.context));

				} else {
					disposers.push(this.on(x.type, x.callback, x.context));
				}
			}
		});

		return new MultiDisposer(disposers);
	}

}
